// src/components/Dashboard/charts/SLATeamComparisonChart.tsx
import React from "react";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";

interface SLATeamComparisonChartProps {
  teamData: any;
}

const SLATeamComparisonChart: React.FC<SLATeamComparisonChartProps> = ({
  teamData,
}) => {
  if (!teamData || !teamData.combined_compliance) {
    return (
      <div className="flex justify-center items-center h-64">
        <p className="text-gray-500">No hay datos de equipos disponibles</p>
      </div>
    );
  }

  const getComplianceColor = (percentage: number) => {
    if (percentage >= 90) return "#10b981"; // Green
    if (percentage >= 80) return "#f59e0b"; // Orange
    if (percentage >= 70) return "#f97316"; // Orange-red
    return "#ef4444"; // Red
  };

  const getComplianceColorClass = (percentage: number) => {
    if (percentage >= 90) return "text-green-600";
    if (percentage >= 80) return "text-yellow-600";
    if (percentage >= 70) return "text-orange-600";
    return "text-red-600";
  };

  const ProgressBar = ({
    percentage,
    color,
  }: {
    percentage: number;
    color: string;
  }) => (
    <div className="w-full bg-gray-200 rounded-full h-3">
      <div
        className="h-3 rounded-full transition-all duration-300"
        style={{
          width: `${Math.min(percentage, 100)}%`,
          backgroundColor: color,
        }}
      />
    </div>
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {teamData.combined_compliance.map((team: any, index: number) => (
        <div
          key={index}
          className="bg-gray-50 rounded-lg p-6 border border-gray-200"
        >
          <div className="text-center mb-6">
            <h3 className="text-xl font-bold text-gray-900 mb-2 capitalize">
              Equipo {team.team_name}
            </h3>
            <div className="flex items-center justify-center mb-3">
              <CheckCircleOutlined
                className="text-2xl mr-2"
                style={{ color: getComplianceColor(team.compliance_pct) }}
              />
              <div
                className={`text-4xl font-bold ${getComplianceColorClass(team.compliance_pct)}`}
              >
                {team.compliance_pct.toFixed(1)}%
              </div>
            </div>
            <div className="text-sm text-gray-500 mb-3">
              Cumplimiento General SLA
            </div>
            <ProgressBar
              percentage={team.compliance_pct}
              color={getComplianceColor(team.compliance_pct)}
            />
          </div>

          {/* Detailed Metrics */}
          <div className="space-y-4">
            {/* Before 1:00 PM */}
            <div className="flex items-center justify-between p-3 bg-white rounded-lg">
              <div className="flex items-center">
                <ClockCircleOutlined className="text-lg mr-3" style={{ color: '#2563eb' }} />
                <div>
                  <div className="font-medium text-gray-900">Primer bloque</div>
                  {/*<div className="text-xs text-gray-500">Meta: 85%</div>*/}
                </div>
              </div>
              <div className="text-right">
                <div className={`text-lg font-bold`}>
                  {team.before_cutoff_pct.toFixed(1)}%
                </div>
              </div>
            </div>

            {/* After 1:00 PM */}
            <div className="flex items-center justify-between p-3 bg-white rounded-lg">
              <div className="flex items-center">
                <ExclamationCircleOutlined className="text-lg mr-3" style={{ color: '#ea580c' }} />
                <div>
                  <div className="font-medium text-gray-900">
                    Segundo bloque
                  </div>
                  {/*<div className="text-xs text-gray-500">Meta: 90%</div>*/}
                </div>
              </div>
              <div className="text-right">
                {/*<div*/}
                {/*  className={`text-lg font-bold ${getComplianceColorClass(team.after_cutoff_pct)}`}*/}
                {/*>*/}
                {/*  {team.after_cutoff_pct.toFixed(1)}%*/}
                {/*</div>*/}
                <div className={`text-lg font-bold`}>
                  {team.after_cutoff_pct.toFixed(1)}%
                </div>
              </div>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">
                  {team.total_processed.toLocaleString()}
                </div>
                <div className="text-sm text-gray-500">Total Procesados</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {team.compliant.toLocaleString()}
                </div>
                <div className="text-sm text-gray-500">Cumplidos</div>
              </div>
            </div>

            {/* Compliance Rate Bar */}
            <div className="text-center text-sm text-gray-600">
              {team.compliant}/{team.total_processed} requisitos
              <div className="text-xs text-gray-500 mt-1">
                (
                {team.total_processed > 0
                  ? ((team.compliant / team.total_processed) * 100).toFixed(1)
                  : 0}
                % de efectividad)
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default SLATeamComparisonChart;
